/**
 * Le statut HTTP d'une page de contenu — pour qu'une page absente le DISE.
 * ============================================================================
 *
 * LE DÉFAUT CORRIGÉ ICI
 * ---------------------
 * Une URL sans contenu rendait **HTTP 200** avec un corps « 404 ». Un humain
 * voyait l'erreur ; une machine lisait « la page existe ». Trois conséquences,
 * toutes constatées le 2026-09-14 :
 *
 * 1. les moteurs indexent ces pages fantômes comme de vraies pages ;
 * 2. toute supervision par code HTTP ment — et c'est exactement la
 *    ressemblance que ce projet traite comme une faute : un tableau vide et un
 *    tableau en panne se ressemblent, une page morte et une page vivante aussi ;
 * 3. le contrôle a été écrit FAUX deux fois avant qu'on trouve le bon marqueur,
 *    précisément parce que le code ne disait rien. Un `404` posé rend le
 *    contrôle trivial : `curl -o /dev/null -w '%{http_code}'`.
 *
 * CE QUE CE MODULE NE FAIT PAS
 * ----------------------------
 * Il ne change **rien au rendu**. La page continue d'afficher son bloc « 404 »
 * exactement comme avant : seul l'en-tête de réponse change. C'est délibéré —
 * `throw createError()` remplacerait la page par celle d'erreur de Nuxt, sur
 * laquelle le greffon du CMS tourne aussi, et ce n'est pas le moment d'y
 * toucher (voir l'incident « 500 Failed to fetch » du 14/09).
 *
 * ⚠ PORTÉE. Cela couvre les pages de CE dépôt (`/dons/*`, `/equipe/*`,
 * `/mediatheque`). L'attrape-tout de la racine — `/une-page-inventee` — est
 * rendu par la couche `@wordup-md/nuxt-layer-shadcn-unocss` et reste en 200.
 * Le corriger demande soit de patcher un paquet vendoré, soit de redéfinir sa
 * page : c'est une décision, pas une retouche.
 */

/**
 * Le statut que mérite ce document, ou `null` s'il n'y a rien à changer.
 *
 * Fonction PURE, et c'est ce qui la rend éprouvable hors ligne. Elle ne
 * connaît ni requête, ni framework.
 */
export function statusForContent(document: unknown): number | null {
  // `undefined` (pas encore chargé) et `null` (introuvable) se traitent pareil
  // côté serveur : au moment du rendu SSR, le contenu est résolu ou absent.
  return document ? null : 404
}

/**
 * Pose le statut sur la réponse quand le document est absent. Rend le statut
 * appliqué, ou `null`.
 *
 * Côté client, il n'y a pas de réponse à annoter : la fonction ne fait rien et
 * le dit en rendant le statut quand même, pour que l'appelant puisse s'en
 * servir dans son titre sans dupliquer le test.
 */
/**
 * À appeler depuis le bloc « 404 » lui-même : **son rendu EST le constat
 * d'absence**. C'est ce qui permet de couvrir l'attrape-tout de la racine, rendu
 * par la couche `@wordup-md/nuxt-layer-shadcn-unocss`, sans forker sa page de
 * 149 lignes — un composant du dépôt l'emporte sur celui de la couche.
 */
export function useMissingContentStatus(): number | null {
  return useContentStatus(null)
}

export function useContentStatus(document: unknown): number | null {
  const status = statusForContent(document)
  if (status !== null && import.meta.server) {
    const event = useRequestEvent()
    if (event) setResponseStatus(event, status)
  }
  return status
}
